import React, { useState, useEffect, useCallback } from 'react';

import {
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_ACTIVATION_ITEM_ORDER_TYPE_DELAYED_ORDERS,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
  SR_API_ACTION_UTILITY_ACTIVATION_NOTE_CREATE,
  SR_API_ACTION_UTILITY_ACTIVATION_ITEM_GET_PURCHASE_REQUEST_MESSAGE,
  SR_API_ACTION_AUTOMATIC_ORDERS_REVIEW_ORDER,
} from 'api/utilityActionTypes';

import useBranch from 'client/hooks/useBranch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import UPActivationNotes from 'client/components/UPActivationNotes/UPActivationNotes';
import UPActivationFiles from 'client/components/UPActivationFiles/UPActivationFiles';
import UPOrderHitlistInternetItemForm from 'client/components/UPOrdersHitlistTable/UPOrderHitlistInternetItemForm';
import UPOrdersHitlistTableRowDelayOrder from 'client/components/UPOrdersHitlistTable/UPOrdersHitlistTableRowDelayOrder';
import UPOrderHitlistTableFailedAutomatedOrderBanner from 'client/components/UPOrdersHitlistTable/UPOrderHitlistTableFailedAutomatedOrderBanner';

import './UPOrdersHitlistTable.less';

const UPOrdersHitlistTableRowBody = ({
  dispatch,
  currentUser,
  orderType,
  utilityActivationId,
  utilityActivationShortId,
  utilityActivationItem,
  automaticOrder,
  utilityDealers,
  utilityDelayedReasons,
  onReloadItem,
  onRemoveItem,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [utilityPurchaseRequestMessageDetails, setUtilityPurchaseRequestMessageDetails] = useState(
    null,
  );
  const [
    showUtilityPurchaseRequestMessageDetails,
    setShowUtilityPurchaseRequestMessageDetails,
  ] = useState(false);

  const { utilityActivation } = useBranch({
    utilityActivation: ['utilityActivations', 'byShortId', utilityActivationShortId],
  });

  const loadData = useCallback(async () => {
    const [utilityActivation] = await Promise.all([
      dispatch(UtilityActivationActions.getByShortId, {
        utilityActivationShortId,
      }),
    ]);

    setIsLoading(false);

    return {
      utilityActivation,
    };
  }, [utilityActivationShortId, dispatch]);

  const onLoadPurchaseRequestMessage = () => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_ITEM_GET_PURCHASE_REQUEST_MESSAGE,
      payload: {
        ...utilityActivationItem.purchaseRequestMessageDetails,
      },
      onError(error) {
        global.alert(error.message);
      },
      onSuccess(slackMessage) {
        setUtilityPurchaseRequestMessageDetails(slackMessage);
        setShowUtilityPurchaseRequestMessageDetails(true);
      },
    });
  };

  const reloadUtilityActivation = useCallback(async () => {
    if (global.document.visibilityState === 'visible') {
      const [utilityActivation] = await Promise.all([
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId,
          forceRefresh: true,
        }),
        onReloadItem({ utilityActivationId }),
      ]);

      const itemUpdated = utilityActivation.items.find(
        (item) => item.id === utilityActivationItem.id,
      );
      if (itemUpdated.status !== UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED) {
        onRemoveItem(utilityActivation);
        global.scrollTo(0, 0);
      }
    }
  }, [
    dispatch,
    onReloadItem,
    onRemoveItem,
    utilityActivationItem,
    utilityActivationId,
    utilityActivationShortId,
  ]);

  const onUpdateUtilityDelayedReason = ({ utilityDelayedReasonId, note }) => {
    const utilityDelayReason = utilityDelayedReasons.find(
      (reason) => reason.id === utilityDelayedReasonId,
    );

    return Promise.all([
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
        payload: {
          id: utilityActivationItem.id,
          updates: {
            utilityDelayedReasonId,
          },
        },
      }),
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_ACTIVATION_NOTE_CREATE,
        payload: {
          utilityActivationId: utilityActivation.id,
          note: `${utilityDelayReason.reason}: ${note}`,
        },
      }),
    ]).then(() => {
      onReloadItem({
        utilityActivationId: utilityActivation.id,
        orderType: UTILITY_ACTIVATION_ITEM_ORDER_TYPE_DELAYED_ORDERS,
      });
    });
  };

  const onVisibilityChange = useCallback(() => {
    reloadUtilityActivation();
  }, [reloadUtilityActivation]);

  const onAcknowledgeAutomatedOrderFailed = ({ automaticOrderId, attemptId, reason }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_AUTOMATIC_ORDERS_REVIEW_ORDER,
      payload: {
        automaticOrderId,
        attemptId,
        failingReason: reason,
      },
    }).then(() => {
      reloadUtilityActivation();
    });
  };

  useEffect(() => {
    loadData().then(() => setIsLoading(false));
  }, [utilityActivationShortId, dispatch, loadData]);

  useEffect(() => {
    global.document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      global.document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [onVisibilityChange]);

  if (isLoading || !utilityActivation) {
    return <div className="loading-ellipses clearfix" />;
  }

  const shouldAskForAutomatedOrderFailedReason =
    automaticOrder.automaticOrderId !== null &&
    automaticOrder.automaticOrderStatus === 'FAILED' &&
    automaticOrder.lastAttempt !== null &&
    automaticOrder.lastAttempt.attemptStatus === 'FAILED' &&
    automaticOrder.lastAttempt.reviewedBy === null;

  return (
    <div styleName="tableRowBody">
      <div>
        {utilityActivationItem.purchaseRequestMessageDetails && (
          <div styleName="purchaseRequestMessageDetails">
            <span
              styleName="link"
              onClick={() => {
                if (!utilityPurchaseRequestMessageDetails) {
                  onLoadPurchaseRequestMessage();
                } else {
                  setShowUtilityPurchaseRequestMessageDetails(
                    !showUtilityPurchaseRequestMessageDetails,
                  );
                }
              }}
            >
              {!utilityPurchaseRequestMessageDetails
                ? 'Load Purchase Request Message Details'
                : showUtilityPurchaseRequestMessageDetails
                ? 'Hide Purchase Request Message Details'
                : 'Show Purchase Request Message Details'}
            </span>
            {showUtilityPurchaseRequestMessageDetails && (
              <>
                {utilityPurchaseRequestMessageDetails.messages[0].attachments[0].fields.map(
                  (field) => (
                    <div styleName="message" key={field.value}>
                      {field.title && <span styleName="title">{field.title}</span>}
                      <span
                        styleName="value"
                        data-dd-privacy={
                          field.title === 'CC Details' ||
                          field.title === 'Provider Specific Questions'
                            ? 'mask'
                            : 'allow'
                        }
                      >
                        {field.value}
                      </span>
                    </div>
                  ),
                )}
              </>
            )}
          </div>
        )}
        {utilityActivation && (
          <>
            {shouldAskForAutomatedOrderFailedReason ? (
              <UPOrderHitlistTableFailedAutomatedOrderBanner
                onSubmit={({ reason }) => {
                  onAcknowledgeAutomatedOrderFailed({
                    automaticOrderId: automaticOrder.automaticOrderId,
                    attemptId: automaticOrder.lastAttempt.id,
                    reason,
                  });
                }}
              />
            ) : (
              <>
                <UPOrderHitlistInternetItemForm
                  dispatch={dispatch}
                  utilityActivation={utilityActivation}
                  utilityDealers={utilityDealers}
                  reloadUtilityActivation={reloadUtilityActivation}
                />
                <UPOrdersHitlistTableRowDelayOrder
                  dispatch={dispatch}
                  currentUser={currentUser}
                  orderType={orderType}
                  utilityActivation={utilityActivation}
                  utilityActivationItem={utilityActivationItem}
                  utilityDelayedReasons={utilityDelayedReasons}
                  onUpdateUtilityDelayedReason={onUpdateUtilityDelayedReason}
                />
              </>
            )}
          </>
        )}
      </div>
      <div>
        {utilityActivation && (
          <>
            <UPActivationNotes utilityActivationId={utilityActivation.id} />
            <UPActivationFiles
              utilityActivationId={utilityActivation.id}
              utilityActivationItemId={utilityActivationItem.id}
            />
          </>
        )}
      </div>
    </div>
  );
};

UPOrdersHitlistTableRowBody.displayName = 'UPOrdersHitlistTableRowBody';

export default UPOrdersHitlistTableRowBody;
